// shared-components/features/admin/AdminNav.jsx

import { AdminNavItem } from "./AdminNavItem";
import { THEME, TYPOGRAPHY } from "@shared/constants/designSystem";
import { useWebsite } from "@shared/hooks";
import { supportsDigitalProducts } from "@shared/utils";
import {
  HomeIcon,
  PhotoIcon,
  ShoppingBagIcon,
  CalendarIcon,
  NewspaperIcon,
  ClipboardDocumentListIcon,
  MegaphoneIcon,
  CloudArrowDownIcon,
  ChartBarIcon,
  CogIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  BanknotesIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";

export const AdminNav = () => {
  const { features } = useWebsite();
  const websiteContext = useWebsite();

  // Helper function to check if a feature is enabled
  const isFeatureEnabled = (feature) => {
    if (!feature) return false;
    if (typeof feature === "boolean") return feature;
    if (typeof feature === "object" && feature.enabled !== undefined)
      return feature.enabled;
    return false;
  };

  // Check if digital products are enabled using consistent utility function
  const isDigitalEnabled = () => {
    return (
      isFeatureEnabled(features?.shop) &&
      supportsDigitalProducts(websiteContext)
    );
  };

  // Build navigation dynamically based on features
  const navigation = [
    { name: "Dashboard", to: "/admin/dashboard", icon: HomeIcon },
    {
      section: "Content",
      items: [
        // Only show Gallery if gallery feature is enabled
        isFeatureEnabled(features?.gallery) && {
          name: "Gallery",
          to: "/admin/gallery",
          icon: PhotoIcon,
        },
        // Only show Products if shop is enabled
        isFeatureEnabled(features?.shop) && {
          name: "Products",
          to: "/admin/shop",
          icon: ShoppingBagIcon,
          badge: isDigitalEnabled() ? "Digital" : null,
        },
        // Only show Tour Dates if tour feature is enabled
        isFeatureEnabled(features?.tour) && {
          name: "Tour Dates",
          to: "/admin/tour",
          icon: CalendarIcon,
        },
        // Only show Media if media feature is enabled
        isFeatureEnabled(features?.media) && {
          name: "Media",
          to: "/admin/media",
          icon: NewspaperIcon,
        },
        // Only show Announcements if announcements feature is enabled
        isFeatureEnabled(features?.announcements) && {
          name: "Announcements",
          to: "/admin/announcements",
          icon: MegaphoneIcon,
        },
      ].filter(Boolean), // Remove any false values
    },
    // Only show Sales section if shop is enabled
    isFeatureEnabled(features?.shop) && {
      section: "Sales",
      items: [
        {
          name: "Orders",
          to: "/admin/orders",
          icon: ClipboardDocumentListIcon,
        },
      ],
    },
    // Digital Products section - only show if digital products are enabled
    isDigitalEnabled() && {
      section: "Digital Music",
      items: [
        {
          name: "Analytics",
          to: "/admin/digital-analytics",
          icon: ChartBarIcon,
          badge: "Insights",
        },
        {
          name: "Downloads",
          to: "/admin/downloads",
          icon: CloudArrowDownIcon,
        },
        {
          name: "Customer Support",
          to: "/admin/customer-support",
          icon: CogIcon,
          badge: "Help",
        },
        {
          name: "Stripe Setup",
          to: "/admin/stripe-connect",
          icon: CreditCardIcon,
        },
        {
          name: "Revenue",
          to: "/admin/revenue",
          icon: BanknotesIcon,
        },
        {
          name: "Email Monitoring",
          to: "/admin/email-monitoring",
          icon: EnvelopeIcon,
          badge: "Delivery",
        },
      ],
    },
    // Settings - always show
    { name: "Settings", to: "/admin/settings", icon: Cog6ToothIcon },
  ].filter(Boolean); // Remove any false values

  return (
    <nav className="space-y-6">
      {navigation.map((item) => (
        <div key={item.section || item.name}>
          {item.section ? (
            <div>
              <h3
                className={`${TYPOGRAPHY.BODY.SMALL} ${THEME.COLORS.TEXT.MUTED} uppercase tracking-wider font-medium px-4 mb-2`}
              >
                {item.section}
              </h3>
              <div className="space-y-1">
                {item.items.map((subItem) => (
                  <AdminNavItem
                    key={subItem.name}
                    to={subItem.to}
                    icon={subItem.icon}
                    badge={subItem.badge}
                  >
                    {subItem.name}
                  </AdminNavItem>
                ))}
              </div>
            </div>
          ) : (
            <AdminNavItem to={item.to} icon={item.icon} badge={item.badge}>
              {item.name}
            </AdminNavItem>
          )}
        </div>
      ))}
    </nav>
  );
};
